import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { resolveCollectorContributionContext } from '@/lib/community-collector.storage';
import { kitLang } from '@/features/contribution-kit';
import {
  AudioAnswerPanel,
  InitialDataSubNav,
  InitialDataWorkspace,
  audioSubmitFeedback,
  initialDataCopy,
  useInitialAudioSession,
} from '@/features/initial-data';
import { CollectionPlaceStep } from '@/features/collection-place';

/**
 * "Audio initial": choose the place, a language and a sector, then answer
 * one question at a time out loud. Also embedded in the collector dashboard
 * (`onLeave` brings the collector back to it). The initial-data sub-nav is
 * offered before a session, except inside the collector workspace.
 */
interface InitialAudioProps {
  onLeave?: () => void;
}

const InitialAudio = ({ onLeave }: InitialAudioProps) => {
  const { language } = useApp();
  const copy = initialDataCopy[kitLang(language)];
  const { toast } = useToast();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const location = useLocation();
  const collectorContext = useMemo(() => resolveCollectorContributionContext(location.state), [location.state]);

  const { setup, catalog, session, recorder, quality, pause, isSubmitting, startSession, submitCurrent, discardRecording, startRecording } =
    useInitialAudioSession(collectorContext);
  const isQualityBlocking = quality.status === 'measuring' || (quality.status === 'ready' && quality.issues.length > 0);

  const currentLang = lang || 'fr';

  const handleSubmit = async () => {
    if (!recorder.answer) {
      toast({ title: copy.audioRequired, variant: 'destructive' });
      return;
    }
    toast(audioSubmitFeedback(copy, await submitCurrent()));
  };

  const handlePause = () => {
    pause.pause();
    toast({ title: copy.pausedToast, description: copy.pausedToastBody });
  };

  return (
    <>
      {!onLeave && setup.step === 'setup' && <InitialDataSubNav />}
      <InitialDataWorkspace
        copy={copy}
        mode="audio"
        step={setup.step}
        languages={setup.languages}
        isLoadingLanguages={setup.isLoadingLanguages}
        language={setup.language}
        languageName={setup.languageName}
        sectorId={setup.sectorId}
        placeStep={<CollectionPlaceStep state={setup.collectionPlace} />}
        isPlaceReady={setup.collectionPlace.place !== null}
        catalog={catalog}
        session={session}
        isSubmitting={isSubmitting}
        canSubmit={Boolean(recorder.answer) && !isQualityBlocking}
        blocked={recorder.isRecording}
        collectorProfileCode={collectorContext?.communityProfileCode}
        recording={{
          isRecording: recorder.isRecording,
          isPaused: recorder.isPaused,
          hasRecording: Boolean(recorder.url) && !recorder.isRecording,
          onStart: startRecording,
          onStop: recorder.stop,
          onPause: recorder.pause,
          onResume: recorder.resume,
          onRedo: discardRecording,
        }}
        answerPanel={
          <AudioAnswerPanel
            copy={copy}
            languageName={setup.languageName}
            isRecording={recorder.isRecording}
            isPaused={recorder.isPaused}
            stream={recorder.stream}
            elapsedMs={recorder.elapsedMs}
            recordedUrl={recorder.url}
            recordedDurationMs={recorder.answer?.durationMs ?? 0}
            micError={recorder.error === 'microphone'}
            quality={quality}
          />
        }
        onLanguageChange={setup.setLanguage}
        onSectorChange={setup.setSectorId}
        onStart={startSession}
        onSubmit={() => void handleSubmit()}
        onChangeSector={() => setup.setStep('setup')}
        savedPosition={pause.saved}
        locale={language}
        onResume={pause.resume}
        onRestart={pause.restart}
        onPause={handlePause}
        onLeave={onLeave ?? (() => navigate(`/${currentLang}`))}
      />
    </>
  );
};

export default InitialAudio;
